import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { Nullable } from 'models/helpers'
import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import { useQuery } from '@tanstack/react-query'
import { JobStatus } from 'constants/API'
import { useAPI } from 'utils/API'
import { logger } from 'utils/logging'

export enum Endpoints {
  AI_VISUALS_MASK_UPLOAD_URL = '/assignment/{assignmentId}/ai-visuals/mask-upload-url',
  AI_VISUALS_AI_ERASER = '/assignment/{assignmentId}/ai-visuals/ai-eraser',
  AI_VISUALS_RESULT = '/assignment/{assignmentId}/ai-visuals/result',
  AI_VISUALS_SAVE_RESULT = '/assignment/{assignmentId}/ai-visuals/save-result-to-gallery',
  AI_VISUALS_SAVE_TEMPORARY_VISUAL = '/assignment/{assignmentId}/ai-visuals/save-temporary-visual',
  AI_VISUALS_VISUAL_STAGING = '/assignment/{assignmentId}/ai-visuals/ai-room-styling',
  AI_VISUALS_WALL_PAINTING = '/assignment/{assignmentId}/ai-visuals/wall-painting',
  AI_VISUALS_FLOOR_REPLACEMENT = '/assignment/{assignmentId}/ai-visuals/floor-replacement',
  AI_VISUALS_DECLUTTER = '/assignment/{assignmentId}/ai-visuals/ai-empty-room',
  AI_VISUALS_AI_ENHANCEMENT = '/assignment/{assignmentId}/ai-visuals/ai-enhancement',
}

export enum PhotoStudioQueryKeys {
  AI_ERASER_RESULT = 'AI_ERASER_RESULT',
  VISUAL_STAGING_RESULT = 'VISUAL_STAGING_RESULT',
  AI_ENHANCEMENT_RESULT = 'AI_ENHANCEMENT_RESULT',
}

interface AIEraserRequest {
  visualFilename?: string
  maskFilename: string
  /**
   * @deprecated Re-execution of the same job (old Photo Studio). Prefer starting a new job with visualFilename + temp bucket.
   */
  reExecuteFromPreviousJob?: string
  useTempBucket: boolean // for using the temp bucket for the AI Eraser
}

interface AIEraserResponse {
  id: string
  status: JobStatus
}

// Visual Staging Types
export interface VisualStagingSettings {
  generationType: string
  style: string
  processingMode: string
  roomType: string
  keepFloor: boolean
}

export interface VisualStagingRequest {
  visualFilename?: string
  maskFilename?: string
  /**
   * @deprecated Re-execution of the same job (old Photo Studio). Prefer starting a new job with visualFilename + temp bucket.
   */
  reExecuteFromPreviousJob?: string
  settings: VisualStagingSettings
  useTempBucket: boolean // for using the temp bucket for the VirtualStaging
}

export interface VisualStagingResponse {
  id: string
  status: JobStatus
}

export interface WallPaintingSettings {
  colors: {
    wall: string
    ceiling: string
    door: string
    floorFrame: string
  }
}

export interface WallPaintingRequest {
  visualFilename?: string
  /**
   * @deprecated Re-execution of the same job (old Photo Studio). Prefer starting a new job with visualFilename + temp bucket.
   */
  reExecuteFromPreviousJob?: string
  settings: WallPaintingSettings
  useTempBucket: boolean // for using the temp bucket for the Wall Painting
}

export interface WallPaintingResponse {
  id: string
  status: JobStatus
}

export interface FloorReplacementRequest {
  visualFilename: string
  floorTemplate: string
}

export interface FloorReplacementResponse {
  id: string
  status: JobStatus
}

export interface DeclutterRequest {
  visualFilename: string
}

export interface DeclutterResponse {
  id: string
  status: JobStatus
}

export interface AIEnhancementRequest {
  visualFilename: string
  skyReplacement?: Nullable<SkyReplacementOption>
}

export interface AIEnhancementResponse {
  id: string
  status: JobStatus
}

export interface AIVisualsResultResponse {
  signedURL: string
  filename: string
  headers: Record<string, string>
  fileCreatedAt: string
}

/**
 * Query for starting AI Eraser processing
 * Accepts visual filename, mask filename, and optional re-execute job ID
 */
export function useAIEraserProcess() {
  const api = useAPI<Endpoints>()

  return getMutation<AIEraserResponse, { assignmentId: string } & AIEraserRequest>(
    async ({ assignmentId, visualFilename, maskFilename, reExecuteFromPreviousJob, useTempBucket }) => {
      // Validate that at least one of visualFilename or reExecuteFromPreviousJob is provided
      const hasVisualFilename = visualFilename && typeof visualFilename === 'string' && visualFilename.trim().length > 0
      const hasReExecuteJob = reExecuteFromPreviousJob && typeof reExecuteFromPreviousJob === 'string' && reExecuteFromPreviousJob.trim().length > 0

      if (!useTempBucket && !hasVisualFilename && !hasReExecuteJob) {
        const error = new Error('photo_studio_editing.error_missing_visual_or_job_id')
        logger.error('AI Eraser: Missing visual filename or previous job ID', error, {
          context: {
            component: 'PhotoStudio',
            action: 'aiEraserProcess',
            metadata: {
              assignmentId,
              hasVisualFilename: !!hasVisualFilename,
              hasReExecuteJob: !!hasReExecuteJob
            }
          }
        })
        throw error
      }

      const requestBody: AIEraserRequest = {
        ...(visualFilename && { visualFilename }),
        maskFilename,
        ...((reExecuteFromPreviousJob && !useTempBucket) && { reExecuteFromPreviousJob }),
        useTempBucket,
      }

      const response = await api.post<AIEraserResponse>(
        Endpoints.AI_VISUALS_AI_ERASER,
        { assignmentId },
        requestBody,
        true
      )
      return response.data
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.AI_VISUAL_ERASER, QueryType.GET, PhotoStudioQueryKeys.AI_ERASER_RESULT] })
    }
  )
}

/**
 * Reusable result retrieval functionality for any AI processing result
 */
export function useReusableAIResult(endpoint: Endpoints, queryKey: string, entityKey: EntityKeys) {
  const api = useAPI<Endpoints>()

  const query = useQuery<AIVisualsResultResponse, Error>({
    queryKey: [entityKey, QueryType.GET, queryKey],
    queryFn: async () => {
      // This will be called via refetch with proper parameters
      throw new Error('Query should be called via refetch with parameters')
    },
    enabled: false
  })

  // Custom refetch function that can be called with parameters
  const refetchWithParams = async (assignmentId: string, jobId: string) => {
    const response = await api.get<AIVisualsResultResponse>(
      endpoint,
      { assignmentId },
      true,
      {
        params: { jobId }
      }
    )
    return response.data
  }

  return {
    ...query,
    refetchWithParams
  }
}

/**
 * Query for getting AI Visuals result after job is finished
 * Returns signed URL and file information for the processed image
 */
export function useAIVisualsResult() {
  return useReusableAIResult(Endpoints.AI_VISUALS_RESULT, PhotoStudioQueryKeys.AI_ERASER_RESULT, EntityKeys.AI_VISUAL_ERASER)
}

/**
 * Query for starting Visual Staging processing
 * Accepts visual filename, mask filename, settings, and optional re-execute job ID
 */
export function useVisualStagingProcess() {
  const api = useAPI<Endpoints>()

  return getMutation<VisualStagingResponse, { assignmentId: string } & VisualStagingRequest>(
    async ({ assignmentId, visualFilename, maskFilename, settings, reExecuteFromPreviousJob, useTempBucket }) => {
      // Validate that at least one of visualFilename or reExecuteFromPreviousJob is provided
      const hasVisualFilename = visualFilename && typeof visualFilename === 'string' && visualFilename.trim().length > 0
      const hasReExecuteJob = reExecuteFromPreviousJob && typeof reExecuteFromPreviousJob === 'string' && reExecuteFromPreviousJob.trim().length > 0

      if (!useTempBucket && !hasVisualFilename && !hasReExecuteJob) {
        const error = new Error('photo_studio_editing.error_missing_visual_or_job_id')
        logger.error('Visual Staging: Missing visual filename or previous job ID', error, {
          context: {
            component: 'PhotoStudio',
            action: 'visualStagingProcess',
            metadata: {
              assignmentId,
              hasVisualFilename: !!hasVisualFilename,
              hasReExecuteJob: !!hasReExecuteJob
            }
          }
        })
        throw error
      }

      const requestBody: VisualStagingRequest = {
        ...(visualFilename && { visualFilename }),
        settings,
        ...(maskFilename && { maskFilename }),
        ...((reExecuteFromPreviousJob && !useTempBucket) && { reExecuteFromPreviousJob }),
        useTempBucket
      }

      const response = await api.post<VisualStagingResponse>(
        Endpoints.AI_VISUALS_VISUAL_STAGING,
        { assignmentId },
        requestBody,
        true
      )
      return response.data
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.AI_VISUAL_ERASER, QueryType.GET, PhotoStudioQueryKeys.VISUAL_STAGING_RESULT] })
    }
  )
}

/**
 * Query for getting Visual Staging result after job is finished
 * Returns signed URL and file information for the processed image
 */
export function useVisualStagingResult() {
  return useReusableAIResult(Endpoints.AI_VISUALS_RESULT, PhotoStudioQueryKeys.VISUAL_STAGING_RESULT, EntityKeys.AI_VISUAL_STAGING)
}

/**
 * Query for starting Wall Painting processing
 * Accepts visual filename, settings, and optional re-execute job ID
 */
export function useWallPaintingProcess() {
  const api = useAPI<Endpoints>()

  return getMutation<WallPaintingResponse, { assignmentId: string } & WallPaintingRequest>(
    async ({ assignmentId, visualFilename, settings, reExecuteFromPreviousJob, useTempBucket }) => {
      // Validate that at least one of visualFilename or reExecuteFromPreviousJob is provided
      const hasVisualFilename = visualFilename && typeof visualFilename === 'string' && visualFilename.trim().length > 0
      const hasReExecuteJob = reExecuteFromPreviousJob && typeof reExecuteFromPreviousJob === 'string' && reExecuteFromPreviousJob.trim().length > 0

      if (!useTempBucket && !hasVisualFilename && !hasReExecuteJob) {
        const error = new Error('photo_studio_editing.error_missing_visual_or_job_id')
        logger.error('Wall Painting: Missing visual filename or previous job ID', error, {
          context: {
            component: 'PhotoStudio',
            action: 'wallPaintingProcess',
            metadata: {
              assignmentId,
              hasVisualFilename: !!hasVisualFilename,
              hasReExecuteJob: !!hasReExecuteJob
            }
          }
        })
        throw error
      }

      const requestBody: WallPaintingRequest = {
        ...(visualFilename && { visualFilename }),
        settings,
        ...((reExecuteFromPreviousJob && !useTempBucket) && { reExecuteFromPreviousJob }),
        useTempBucket
      }

      const response = await api.post<WallPaintingResponse>(
        Endpoints.AI_VISUALS_WALL_PAINTING,
        { assignmentId },
        requestBody,
        true
      )
      return response.data
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.AI_VISUAL_ERASER, QueryType.GET, PhotoStudioQueryKeys.AI_ERASER_RESULT] })
    }
  )
}

/**
 * Query for starting Floor Replacement processing
 * Accepts visual filename and floor template
 */
export function useFloorReplacementProcess() {
  const api = useAPI<Endpoints>()

  return getMutation<FloorReplacementResponse, { assignmentId: string } & FloorReplacementRequest>(
    async ({ assignmentId, visualFilename, floorTemplate }) => {
      if (!visualFilename || typeof visualFilename !== 'string' || visualFilename.trim().length === 0) {
        const error = new Error('photo_studio_editing.error_missing_visual')
        logger.error('Floor Replacement: Missing visual filename', error, {
          context: {
            component: 'PhotoStudio',
            action: 'floorReplacementProcess',
            metadata: {
              assignmentId,
              hasVisualFilename: !!visualFilename
            }
          }
        })
        throw error
      }

      if (!floorTemplate || typeof floorTemplate !== 'string' || floorTemplate.trim().length === 0) {
        const error = new Error('photo_studio_editing.error_missing_floor_template')
        logger.error('Floor Replacement: Missing floor template', error, {
          context: {
            component: 'PhotoStudio',
            action: 'floorReplacementProcess',
            metadata: {
              assignmentId,
              hasFloorTemplate: !!floorTemplate
            }
          }
        })
        throw error
      }

      const requestBody: FloorReplacementRequest = {
        visualFilename,
        floorTemplate,
      }

      const response = await api.post<FloorReplacementResponse>(
        Endpoints.AI_VISUALS_FLOOR_REPLACEMENT,
        { assignmentId },
        requestBody,
        true
      )
      return response.data
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.AI_VISUAL_ERASER, QueryType.GET, PhotoStudioQueryKeys.AI_ERASER_RESULT] })
    }
  )
}

/**
 * Query for starting Declutter processing
 * Accepts visual filename only - no settings needed
 */
export function useDeclutterProcess() {
  const api = useAPI<Endpoints>()

  return getMutation<DeclutterResponse, { assignmentId: string } & DeclutterRequest>(
    async ({ assignmentId, visualFilename }) => {
      if (!visualFilename || typeof visualFilename !== 'string' || visualFilename.trim().length === 0) {
        const error = new Error('photo_studio_editing.error_missing_visual')
        logger.error('Declutter: Missing visual filename', error, {
          context: {
            component: 'PhotoStudio',
            action: 'declutterProcess',
            metadata: {
              assignmentId,
              hasVisualFilename: !!visualFilename
            }
          }
        })
        throw error
      }

      const requestBody: DeclutterRequest = {
        visualFilename,
      }

      const response = await api.post<DeclutterResponse>(
        Endpoints.AI_VISUALS_DECLUTTER,
        { assignmentId },
        requestBody,
        true
      )
      return response.data
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.AI_VISUAL_ERASER, QueryType.GET, PhotoStudioQueryKeys.AI_ERASER_RESULT] })
    }
  )
}

/**
 * Query for starting AI Enhancement processing
 * Accepts visual filename and optional sky replacement option
 */
export function useAIEnhancementProcess() {
  const api = useAPI<Endpoints>()

  return getMutation<AIEnhancementResponse, { assignmentId: string } & AIEnhancementRequest>(
    async ({ assignmentId, visualFilename, skyReplacement }) => {
      if (!visualFilename || typeof visualFilename !== 'string' || visualFilename.trim().length === 0) {
        const error = new Error('photo_studio_editing.error_missing_visual')
        logger.error('AI Enhancement: Missing visual filename', error, {
          context: {
            component: 'PhotoStudio',
            action: 'aiEnhancementProcess',
            metadata: {
              assignmentId,
              hasVisualFilename: !!visualFilename
            }
          }
        })
        throw error
      }

      const requestBody: AIEnhancementRequest = {
        visualFilename,
        ...(skyReplacement && { skyReplacement }),
      }

      const response = await api.post<AIEnhancementResponse>(
        Endpoints.AI_VISUALS_AI_ENHANCEMENT,
        { assignmentId },
        requestBody,
        true
      )
      return response.data
    },
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.AI_VISUAL_ENHANCEMENT, QueryType.GET, PhotoStudioQueryKeys.AI_ENHANCEMENT_RESULT] })
    }
  )
}

/**
 * Query for getting AI Enhancement result after job is finished
 * Returns signed URL and file information for the processed image
 */
export function useAIEnhancementResult() {
  return useReusableAIResult(Endpoints.AI_VISUALS_RESULT, PhotoStudioQueryKeys.AI_ENHANCEMENT_RESULT, EntityKeys.AI_VISUAL_ENHANCEMENT)
}
